import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Input, Button, message, Avatar, Typography, Spin, Modal } from "antd";
import {
  SendOutlined,
  UserOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  BellOutlined,
  BellFilled,
  PaperClipOutlined,
  BulbOutlined,
  TeamOutlined,
  BuildOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { trackEvent } from "../helpers/mixpanel";
import Logo from "../components/general/Logo";
import logoImage from "../assets/logo.png";
import { getTenant } from "../lib/firebase/tenantModel";
import unitModel from "../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { chatModel } from "../lib/firebase/chatModel";
import HandymanModel from "../lib/firebase/handymanModel";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { chatKeys } from "../lib/queryKeys";
import ImageUpload from "../components/chat/ImageUpload";
import HealthConsentModal from "../components/compliance/HealthConsentModal";
import TenantChatPublicLeo from "./TenantChatPublicLeo";
import {
  ChatContainer,
  ChatHeader,
  HeaderContent,
  LogoImage,
  HeaderTitle,
  ParticipantsList,
  ParticipantItem,
  ParticipantIconWrapper,
  ParticipantTags,
  ParticipantTag,
  NotificationSettings,
  NotificationButton,
  NotificationStatus,
  MessagesContainer,
  MessageBubble,
  MessageAvatar,
  MessageContent,
  MessageHeader,
  MessageSender,
  MessageTime,
  MessageText,
  TypingIndicator,
  TypingDots,
  InputArea,
  InputContainer,
  SendButton,
  Disclaimer,
  DisclaimerText,
  ActionIndicator,
  ActionBadge,
  LoadingSpinnerContainer,
  LoadingText,
  ChatUnavailablePage,
  ChatUnavailableLogoBar,
  ChatUnavailableMain,
  ChatUnavailableIconWrap,
  BlockedTitle,
  SubscriptionCanceledAlert,
  ImageUploadSection,
  InputActions,
  AttachButton,
  MessageImages,
  MessageImage,
  renderLeoMarkdown,
} from "../components/chat/tenantChatSharedStyles";

const { TextArea } = Input;
const { Title, Text } = Typography;

function TenantChatWithToken() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const predefinedIntent = searchParams.get("intent");
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingInitialMessage, setIsLoadingInitialMessage] = useState(true);
  const [initialMessageError, setInitialMessageError] = useState(false);
  const [hasLoadedInitialMessage, setHasLoadedInitialMessage] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [notificationPermission, setNotificationPermission] = useState(
    "default"
  );
  const [selectedImages, setSelectedImages] = useState([]);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [healthConsentModalVisible, setHealthConsentModalVisible] = useState(
    false
  );
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastMessageTimeRef = useRef(null);
  const queryClient = useQueryClient();
  const messagesContainerRef = useRef(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const shouldForceScrollRef = useRef(false);
  /** Landlord's SaaS subscription ended - block Leo chat per product policy */
  const [
    landlordSubscriptionCanceled,
    setLandlordSubscriptionCanceled,
  ] = useState(false);

  // Debug mode (local screenshots)
  // Enable via: localStorage.setItem('debug-tenant-chat','true') OR add ?debug=tenant-chat to the URL
  const isDebugTenantChat = (() => {
    if (typeof window === "undefined") return false;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToggle = urlParams.get("debug");
      const lsToggle = localStorage.getItem("debug-tenant-chat") === "true";
      const active =
        lsToggle || urlToggle === "tenant-chat" || urlToggle === "true";
      if (active) {
        console.log("[TenantChat] Debug mode active: tenant-chat");
      }
      return active;
    } catch (e) {
      return false;
    }
  })();

  // Extract token from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (!token && !isDebugTenantChat) {
      console.error("No access token provided in URL");
      navigate("/access-denied");
      return;
    }

    setAccessToken(token);
  }, [navigate, isDebugTenantChat]);

  // Fetch tenant data using token (for email link access)
  // Falls back to Firestore if API endpoint doesn't exist
  const fetchTenantWithToken = useCallback(
    async (tenantId, token) => {
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";

      // Try API endpoint if available, otherwise fall back to Firestore
      if (apiUrl) {
        try {
          const response = await fetch(
            `${apiUrl}/tenant-chat/${tenantId}/welcome?token=${token}`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }
          );

          if (response.status === 401) {
            const error = await response.json();
            console.error("Token expired or invalid:", error);
            navigate("/token-expired");
            throw new Error("Token expired or invalid");
          }

          if (response.status === 403) {
            const err = await response.json().catch(() => ({}));
            if (
              err.code === "LANDLORD_TENANT_COMMS_BLOCKED" ||
              err.code === "LANDLORD_SUBSCRIPTION_CANCELED"
            ) {
              setLandlordSubscriptionCanceled(true);
              throw new Error("LANDLORD_TENANT_COMMS_BLOCKED");
            }
          }

          if (response.ok) {
            const data = await response.json();

            // The welcome endpoint returns tenantInfo, extract it
            if (data.tenantInfo) {
              return data.tenantInfo;
            }

            // Fallback: return data if it's already in tenant format
            if (data.tenant) {
              return data.tenant;
            }

            // If structure is different, return the data as-is
            return data;
          }

          // If endpoint doesn't exist (404), fall through to Firestore
          if (response.status === 404) {
            console.log(
              "[TenantChat] API endpoint not found, falling back to Firestore"
            );
          }
        } catch (error) {
          if (
            error.message === "LANDLORD_TENANT_COMMS_BLOCKED" ||
            error.message === "LANDLORD_SUBSCRIPTION_CANCELED"
          ) {
            throw error;
          }
          // If fetch fails (network error, etc.), fall back to Firestore
          if (
            error.message?.includes("Token expired") ||
            error.message?.includes("Token invalid")
          ) {
            throw error; // Re-throw auth errors
          }
          console.log(
            "[TenantChat] API call failed, falling back to Firestore:",
            error.message
          );
        }
      }

      // Fallback: Use Firestore directly (original behavior)
      // Token is still in URL for access control, but we fetch tenant data from Firestore
      console.log("[TenantChat] Using Firestore to fetch tenant data");
      return await getTenant(tenantId);
    },
    [navigate]
  );

  // Load tenant data - use token-based fetch if token is present, otherwise use authenticated method
  const { data: tenantData, isLoading: isTenantLoading } = useQuery({
    queryKey: ["tenant", tenantId, accessToken],
    queryFn: async () => {
      // If we have a token, use token-based fetch (for email link access)
      if (accessToken) {
        return await fetchTenantWithToken(tenantId, accessToken);
      }
      // Otherwise, use authenticated method (for logged-in users)
      return await getTenant(tenantId);
    },
    enabled: !!tenantId && (!!accessToken || isDebugTenantChat),
    retry: (failureCount, error) => {
      // Don't retry on token errors
      if (
        error?.message?.includes("Token expired") ||
        error?.message?.includes("Token invalid")
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Get or create chat for this tenant
  const { data: chatId, isLoading: isChatLoading } = useQuery({
    queryKey: ["chat", tenantId],
    queryFn: async () => {
      // If tenantData is available, use it
      if (tenantData) {
        return await chatModel.getOrCreateChat(tenantId, tenantData.ownerId, {
          name: tenantData.name,
          email: tenantData.email || "",
          unitNumber: tenantData.unitNumber || "N/A",
          unitAddress: await getUnitAddress(tenantData),
          landlordName: "Landlord",
        });
      }

      // Fallback: try to create chat with minimal info if tenantData failed
      // This allows chat to work even when backend API is unavailable
      if (tenantId) {
        console.log(
          "[TenantChat] Creating chat with minimal info due to API failure"
        );
        try {
          // Try to get ownerId from tenantInfo state if available
          const ownerId = tenantInfo?.ownerId || "unknown";
          return await chatModel.getOrCreateChat(tenantId, ownerId, {
            name: tenantInfo?.name || "Tenant",
            email: tenantInfo?.email || "",
            unitNumber: tenantInfo?.unitNumber || "N/A",
            unitAddress: tenantInfo?.unitAddress || "Address not available",
            landlordName: "Landlord",
          });
        } catch (error) {
          console.error(
            "[TenantChat] Failed to create chat with fallback:",
            error
          );
          return null;
        }
      }

      return null;
    },
    enabled: !!tenantId && (!!tenantData || !!tenantInfo),
    retry: false, // Don't retry if it fails
  });

  // Load messages for this chat - use backend API if conversationId is available
  const {
    data: messages = [],
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: conversationId
      ? ["tenantBackendMessages", conversationId]
      : chatKeys.messages(chatId),
    queryFn: async () => {
      // If we have a conversationId from backend, use backend API
      if (conversationId) {
        const apiUrl =
          process.env.REACT_APP_API_URL || "http://localhost:4000/api";
        if (!apiUrl) {
          console.warn(
            "[TenantChat] REACT_APP_API_URL not configured, falling back to Firestore"
          );
          return chatModel.getMessages(chatId);
        }

        try {
          console.log("[TenantChat] Fetching messages from backend API:", {
            conversationId,
            url: `${apiUrl}/conversations/${conversationId}`,
          });

          // CRITICAL: Log conversationId and tenantId to verify matching with landlord's chat
          console.log(
            "🔍🔍🔍 [TenantChat] TENANT FETCHING CONVERSATION - RECORD THIS INFO:",
            {
              conversationId: conversationId,
              tenantId: tenantId,
              tenantOwnerId: tenantData?.ownerId,
              landlordId: tenantData?.ownerId,
              timestamp: new Date().toISOString(),
              url: `${apiUrl}/conversations/${conversationId}`,
            }
          );

          console.warn(
            "⚠️ DIAGNOSTIC: Tenant is viewing conversationId:",
            conversationId,
            "- Does this match landlord's conversationId?"
          );

          // Request all messages (no limit) for tenant chat
          const response = await fetch(
            `${apiUrl}/conversations/${conversationId}?limit=1000&includeAllMessages=true`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch conversation: ${response.status}`);
          }

          const data = await response.json();
          const conversation = data.success ? data.conversation : null;

          // Log raw response for debugging
          console.log("[TenantChat] Raw backend response:", {
            success: data.success,
            hasConversation: !!conversation,
            conversationId: conversation?.id,
            hasMessages: !!conversation?.messages,
            messagesType: Array.isArray(conversation?.messages)
              ? "array"
              : typeof conversation?.messages,
            messagesLength: Array.isArray(conversation?.messages)
              ? conversation.messages.length
              : "N/A",
            messagesValue: conversation?.messages,
            conversationKeys: conversation ? Object.keys(conversation) : [],
            rawData: data,
          });

          // If conversation exists but no messages, log the full conversation structure
          if (
            conversation &&
            (!conversation.messages || conversation.messages.length === 0)
          ) {
            console.error("[TenantChat] ❌❌❌ CONVERSATION HAS NO MESSAGES!", {
              conversationId: conversation.id,
              conversationStructure: conversation,
              allKeys: Object.keys(conversation),
              messagesField: conversation.messages,
              messagesType: typeof conversation.messages,
              isArray: Array.isArray(conversation.messages),
              rawResponse: data,
            });
          }

          // CRITICAL: Log if conversationId matches what we requested
          if (conversation && conversation.id !== conversationId) {
            console.error("[TenantChat] ❌ CONVERSATION ID MISMATCH!", {
              requestedId: conversationId,
              receivedId: conversation.id,
              tenantId: tenantId,
              conversationTenantId:
                conversation.tenantId || conversation.context?.tenantId,
            });
          }

          // Check if messages array exists and is valid
          if (!conversation) {
            console.error("[TenantChat] ❌ No conversation in response!", {
              data,
            });
            return [];
          }

          if (!conversation.messages) {
            console.error(
              "[TenantChat] ❌ No messages array in conversation!",
              {
                conversationId: conversation.id,
                conversationKeys: Object.keys(conversation),
                conversation: conversation,
              }
            );
            return [];
          }

          if (!Array.isArray(conversation.messages)) {
            console.error("[TenantChat] ❌ Messages is not an array!", {
              conversationId: conversation.id,
              messagesType: typeof conversation.messages,
              messagesValue: conversation.messages,
            });
            return [];
          }

          if (conversation.messages.length === 0) {
            console.warn(
              "[TenantChat] ⚠️ Backend returned empty messages array! Falling back to Firestore.",
              {
                conversationId: conversation.id,
                conversation: conversation,
              }
            );
            // Fallback to Firestore if backend returns empty messages
            try {
              const firestoreMessages = await chatModel.getMessages(chatId);
              console.log(
                "[TenantChat] Firestore fallback returned",
                firestoreMessages.length,
                "messages"
              );
              return firestoreMessages;
            } catch (firestoreError) {
              console.error(
                "[TenantChat] Firestore fallback also failed:",
                firestoreError
              );
              return [];
            }
          }

          if (conversation && conversation.messages) {
            // Log message breakdown
            const messagesByType = {
              ai: conversation.messages.filter((m) => m.fromUserType === "ai")
                .length,
              tenant: conversation.messages.filter(
                (m) => m.fromUserType === "tenant"
              ).length,
              landlord: conversation.messages.filter(
                (m) => m.fromUserType === "landlord"
              ).length,
              vendor: conversation.messages.filter(
                (m) => m.fromUserType === "vendor"
              ).length,
              other: conversation.messages.filter(
                (m) =>
                  !["ai", "tenant", "landlord", "vendor"].includes(
                    m.fromUserType
                  )
              ).length,
            };

            console.log("[TenantChat] Backend conversation response:", {
              conversationId: conversation.id,
              requestedConversationId: conversationId, // Show what we requested
              conversationType: conversation.type,
              tenantId: conversation.tenantId || conversation.context?.tenantId, // Show tenantId from backend
              ourTenantId: tenantId, // Show our tenantId
              totalMessages: conversation.messages.length,
              messagesByType,
              participants: conversation.participants?.map((p) => ({
                userId: p.userId,
                userType: p.userType,
              })),
              // CRITICAL: Check if landlord messages exist but are being filtered
              landlordMessageCount: messagesByType.landlord,
              allMessageTypes: Object.keys(messagesByType),
            });

            // WARN if we have tenant messages but no landlord messages (landlord may have sent messages)
            if (
              messagesByType.tenant > 0 &&
              messagesByType.landlord === 0 &&
              conversation.messages.length > 1
            ) {
              console.warn(
                "⚠️⚠️⚠️ [TenantChat] POTENTIAL BUG: Tenant has sent messages but NO landlord messages found!",
                {
                  conversationId: conversation.id,
                  tenantMessageCount: messagesByType.tenant,
                  landlordMessageCount: messagesByType.landlord,
                  totalMessages: conversation.messages.length,
                  message:
                    "This suggests landlord messages may not be saved or are being filtered by the backend",
                }
              );
            }

            // WARN if backend is returning suspiciously few messages (likely a backend bug)
            if (
              conversation.messages.length <= 2 &&
              conversation.messages.length > 0
            ) {
              console.warn(
                "⚠️⚠️⚠️ [TenantChat] BACKEND RETURNING ONLY",
                conversation.messages.length,
                "MESSAGES!",
                {
                  conversationId: conversation.id,
                  messageCount: conversation.messages.length,
                  messageIds: conversation.messages.map(
                    (m) => m.id || m.messageId
                  ),
                  messageTypes: messagesByType,
                  warning:
                    "Backend may be limiting messages incorrectly. This is likely a backend bug.",
                  suggestion:
                    "Backend should return ALL messages for tenant conversations, not just the last 2.",
                }
              );
            }

            // CRITICAL: Warn if conversationId doesn't match
            if (conversation.id !== conversationId) {
              console.error("[TenantChat] ❌ CONVERSATION ID MISMATCH!", {
                requestedId: conversationId,
                receivedId: conversation.id,
                tenantId: tenantId,
                conversationTenantId:
                  conversation.tenantId || conversation.context?.tenantId,
              });
            }

            // CRITICAL: Warn if no landlord messages but we expect them
            if (messagesByType.landlord === 0 && messagesByType.tenant > 0) {
              console.error(
                "❌❌❌ [TenantChat] CRITICAL BUG: No landlord messages found!",
                {
                  conversationId: conversation.id,
                  requestedConversationId: conversationId,
                  tenantId: tenantId,
                  landlordId: tenantData?.ownerId,
                  totalMessages: conversation.messages.length,
                  messageTypes: messagesByType,
                  participants: conversation.participants?.map((p) => ({
                    userId: p.userId,
                    userType: p.userType,
                  })),
                  timestamp: new Date().toISOString(),
                }
              );

              console.error("🚨 DIAGNOSTIC CHECKLIST:");
              console.error(
                "1. Check if landlord sent message to conversationId:",
                conversationId
              );
              console.error(
                "2. Check Firestore conversations/" +
                  conversationId +
                  "/messages for landlord messages"
              );
              console.error("3. Check backend logs for filtering logic");
              console.error("4. Landlord userId:", tenantData?.ownerId);
              console.error(
                "5. If messages exist in Firestore but not here, backend is FILTERING them!"
              );
            }

            // Transform backend messages to match frontend format
            const transformedMessages = conversation.messages
              .map((msg, index) => {
                try {
                  // Handle different possible image field names
                  let messageImages = [];
                  if (msg.images && Array.isArray(msg.images)) {
                    messageImages = msg.images;
                  } else if (
                    msg.content?.images &&
                    Array.isArray(msg.content.images)
                  ) {
                    messageImages = msg.content.images;
                  } else if (
                    msg.message?.images &&
                    Array.isArray(msg.message.images)
                  ) {
                    messageImages = msg.message.images;
                  } else if (msg.imageUrls && Array.isArray(msg.imageUrls)) {
                    messageImages = msg.imageUrls;
                  } else if (
                    msg.attachments &&
                    Array.isArray(msg.attachments)
                  ) {
                    messageImages = msg.attachments;
                  }

                  // Extract content with better handling for various formats
                  let extractedContent = "";
                  if (typeof msg.content === "object" && msg.content !== null) {
                    extractedContent =
                      msg.content?.text ||
                      msg.content?.content ||
                      msg.content?.message ||
                      "";
                  } else if (typeof msg.content === "string") {
                    extractedContent = msg.content;
                  } else {
                    extractedContent = msg.message || msg.text || "";
                  }

                  // Log first AI message specifically for debugging
                  if (
                    (msg.fromUserType === "ai" || msg.sender === "ai") &&
                    index === 0
                  ) {
                    console.log(
                      "[TenantChat] 🔍 First AI message transformation:",
                      {
                        index,
                        originalMsg: msg,
                        fromUserType: msg.fromUserType,
                        sender: msg.sender,
                        contentType: typeof msg.content,
                        content: msg.content,
                        message: msg.message,
                        extractedContent,
                        extractedContentLength: extractedContent?.length,
                      }
                    );
                  }

                  const transformed = {
                    id: msg.id || msg.messageId || `temp-${index}`,
                    sender: msg.fromUserType || msg.sender || "tenant",
                    fromUserType: msg.fromUserType, // Preserve for getSenderName lookup
                    fromUserId: msg.fromUserId, // Preserve for getSenderName lookup
                    content: extractedContent,
                    timestamp: msg.timestamp
                      ? new Date(msg.timestamp)
                      : msg.createdAt
                      ? new Date(msg.createdAt)
                      : new Date(),
                    images: messageImages,
                    metadata: msg.metadata || {},
                    repairTicketId:
                      msg.repairTicketId || msg.metadata?.repairTicketId,
                  };

                  // Log landlord messages specifically for debugging
                  if (
                    transformed.sender === "landlord" ||
                    transformed.fromUserType === "landlord"
                  ) {
                    console.log("[TenantChat] Landlord message found:", {
                      id: transformed.id,
                      sender: transformed.sender,
                      fromUserType: transformed.fromUserType,
                      content: transformed.content?.substring(0, 50),
                      timestamp: transformed.timestamp,
                      originalMsg: msg,
                    });
                  }

                  return transformed;
                } catch (error) {
                  console.error("[TenantChat] Error transforming message:", {
                    error: error.message,
                    messageIndex: index,
                    message: msg,
                    stack: error.stack,
                  });
                  // Return a fallback message object to prevent crash
                  return {
                    id: msg.id || msg.messageId || `error-${index}`,
                    sender: "tenant",
                    fromUserType: msg.fromUserType || "tenant",
                    fromUserId: msg.fromUserId,
                    content: "Error loading message",
                    timestamp: new Date(),
                    images: [],
                    metadata: {},
                    error: true,
                  };
                }
              })
              .filter((msg) => msg !== null && msg !== undefined); // Filter out any null/undefined messages

            // Log final transformed messages breakdown
            const transformedByType = {
              ai: transformedMessages.filter((m) => m.sender === "ai").length,
              tenant: transformedMessages.filter((m) => m.sender === "tenant")
                .length,
              landlord: transformedMessages.filter(
                (m) => m.sender === "landlord"
              ).length,
              vendor: transformedMessages.filter((m) => m.sender === "vendor")
                .length,
              other: transformedMessages.filter(
                (m) =>
                  !["ai", "tenant", "landlord", "vendor"].includes(m.sender)
              ).length,
            };

            console.log("[TenantChat] Transformed messages - BREAKDOWN:", {
              total: transformedMessages.length,
              byType: transformedByType,
              landlordMessages: transformedMessages
                .filter((m) => m.sender === "landlord")
                .map((m) => ({
                  id: m.id,
                  sender: m.sender,
                  fromUserType: m.fromUserType,
                  content: m.content?.substring(0, 50),
                })),
            });

            // Sort messages by timestamp to ensure chronological order
            const sortedMessages = transformedMessages.sort((a, b) => {
              const timeA =
                a.timestamp instanceof Date
                  ? a.timestamp.getTime()
                  : new Date(a.timestamp).getTime();
              const timeB =
                b.timestamp instanceof Date
                  ? b.timestamp.getTime()
                  : new Date(b.timestamp).getTime();
              return timeA - timeB;
            });

            // Final check - if we have 0 messages but backend said there were messages, log error
            if (
              sortedMessages.length === 0 &&
              conversation.messages.length > 0
            ) {
              console.error(
                "[TenantChat] ❌ CRITICAL: Transformation resulted in 0 messages but backend returned",
                conversation.messages.length,
                "messages!",
                {
                  originalMessages: conversation.messages,
                  transformedMessages: transformedMessages,
                }
              );
            }

            // CRITICAL FIX: If backend is returning suspiciously few messages (<= 2),
            // fall back to Firestore to get full message history
            // This prevents chat history from disappearing when backend has a bug
            // We check for <= 2 because the backend bug consistently returns only 2 messages
            if (sortedMessages.length <= 2 && sortedMessages.length > 0) {
              console.warn(
                "[TenantChat] ⚠️ Backend returned only",
                sortedMessages.length,
                "messages. Falling back to Firestore for full history.",
                {
                  conversationId: conversation.id,
                  backendMessageCount: sortedMessages.length,
                  backendMessageIds: sortedMessages.map((m) => m.id),
                }
              );

              try {
                const firestoreMessages = await chatModel.getMessages(
                  chatId,
                  null,
                  1000
                ); // Get up to 1000 messages from Firestore
                console.log(
                  "[TenantChat] Firestore fallback returned",
                  firestoreMessages.length,
                  "messages"
                );

                if (firestoreMessages.length > sortedMessages.length) {
                  console.log(
                    "[TenantChat] Using Firestore messages (",
                    firestoreMessages.length,
                    ") instead of backend messages (",
                    sortedMessages.length,
                    ")"
                  );

                  // Transform Firestore messages to match the expected format
                  const transformedFirestoreMessages = firestoreMessages.map(
                    (msg) => ({
                      id: msg.id,
                      sender: msg.sender || "tenant",
                      fromUserType: msg.sender || "tenant",
                      fromUserId:
                        msg.sender === "landlord"
                          ? tenantData?.ownerId
                          : tenantId,
                      content: msg.content || msg.message || "",
                      timestamp:
                        msg.timestamp instanceof Date
                          ? msg.timestamp
                          : msg.timestamp?.toDate?.() ||
                            new Date(msg.timestamp),
                      images: msg.images || [],
                      metadata: msg.metadata || {},
                    })
                  );

                  // Sort Firestore messages chronologically
                  const sortedFirestoreMessages = transformedFirestoreMessages.sort(
                    (a, b) => {
                      const timeA =
                        a.timestamp instanceof Date
                          ? a.timestamp.getTime()
                          : new Date(a.timestamp).getTime();
                      const timeB =
                        b.timestamp instanceof Date
                          ? b.timestamp.getTime()
                          : new Date(b.timestamp).getTime();
                      return timeA - timeB;
                    }
                  );

                  return sortedFirestoreMessages;
                } else {
                  console.log(
                    "[TenantChat] Firestore has same or fewer messages, using backend messages"
                  );
                  return sortedMessages;
                }
              } catch (firestoreError) {
                console.error(
                  "[TenantChat] Firestore fallback failed, using backend messages:",
                  firestoreError
                );
                return sortedMessages; // Return what we have from backend
              }
            }

            // ADDITIONAL FIX: If backend returns more than 2 but still seems incomplete,
            // check Firestore to see if we're missing messages
            // This handles cases where backend returns 3-5 messages but there are actually more
            if (sortedMessages.length > 2 && sortedMessages.length <= 5) {
              try {
                const firestoreMessages = await chatModel.getMessages(
                  chatId,
                  null,
                  1000
                );
                if (firestoreMessages.length > sortedMessages.length * 1.5) {
                  // Firestore has significantly more messages, use it instead
                  console.warn(
                    "[TenantChat] ⚠️ Backend returned",
                    sortedMessages.length,
                    "messages but Firestore has",
                    firestoreMessages.length,
                    ". Using Firestore for complete history."
                  );

                  const transformedFirestoreMessages = firestoreMessages.map(
                    (msg) => ({
                      id: msg.id,
                      sender: msg.sender || "tenant",
                      fromUserType: msg.sender || "tenant",
                      fromUserId:
                        msg.sender === "landlord"
                          ? tenantData?.ownerId
                          : tenantId,
                      content: msg.content || msg.message || "",
                      timestamp:
                        msg.timestamp instanceof Date
                          ? msg.timestamp
                          : msg.timestamp?.toDate?.() ||
                            new Date(msg.timestamp),
                      images: msg.images || [],
                      metadata: msg.metadata || {},
                    })
                  );

                  const sortedFirestoreMessages = transformedFirestoreMessages.sort(
                    (a, b) => {
                      const timeA =
                        a.timestamp instanceof Date
                          ? a.timestamp.getTime()
                          : new Date(a.timestamp).getTime();
                      const timeB =
                        b.timestamp instanceof Date
                          ? b.timestamp.getTime()
                          : new Date(b.timestamp).getTime();
                      return timeA - timeB;
                    }
                  );

                  return sortedFirestoreMessages;
                }
              } catch (firestoreError) {
                // If Firestore check fails, just use backend messages
                console.warn(
                  "[TenantChat] Could not verify Firestore message count:",
                  firestoreError
                );
              }
            }

            return sortedMessages;
          }

          return [];
        } catch (error) {
          console.error(
            "[TenantChat] Failed to fetch from backend API, falling back to Firestore:",
            error
          );
          // Fallback to Firestore if backend fails
          return chatModel.getMessages(chatId);
        }
      }

      // Fallback to Firestore if no conversationId
      return chatModel.getMessages(chatId);
    },
    enabled: !!chatId && !isDebugTenantChat,
    refetchInterval: isDebugTenantChat ? undefined : 5000, // disable polling in debug mode
  });

  // Fetch conversation data from backend API to get participants
  const { data: backendConversationData } = useQuery({
    queryKey: ["tenantBackendConversation", conversationId],
    queryFn: async () => {
      if (!conversationId) return null;
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      if (!apiUrl) return null;

      try {
        // Request all messages (no limit) for tenant chat
        const response = await fetch(
          `${apiUrl}/conversations/${conversationId}?limit=1000&includeAllMessages=true`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) return null;

        const data = await response.json();
        return data.success ? data.conversation : null;
      } catch (error) {
        console.error("[TenantChat] Failed to fetch conversation data:", error);
        return null;
      }
    },
    enabled: !!conversationId,
    refetchInterval: 10000, // Refetch every 10 seconds
  });

  // Get participants from backend conversation or fallback to Firestore
  const conversationParticipants = useMemo(() => {
    let participants = [];

    if (backendConversationData?.participants) {
      participants = backendConversationData.participants;
    } else {
      // Fallback: construct from Firestore chatData
      if (tenantData) {
        participants.push({
          userId: tenantData.id,
          userType: "tenant",
          userName: tenantData.name || "Tenant",
        });
      }
      if (tenantData?.ownerId) {
        participants.push({
          userId: tenantData.ownerId,
          userType: "landlord",
          userName: "Landlord",
        });
      }
    }

    // Filter out disabled participants
    const disabledParticipants =
      backendConversationData?.disabledParticipants || [];
    const disabledUserIds = new Set(
      disabledParticipants.map((p) => p.userId || p)
    );

    // Filter participants: exclude those that are disabled or have enabled: false
    return participants.filter((p) => {
      // If participant has an enabled field, respect it
      if (p.enabled === false) {
        return false;
      }
      // If participant is in the disabledParticipants list, exclude it
      if (disabledUserIds.has(p.userId)) {
        return false;
      }
      return true;
    });
  }, [backendConversationData, tenantData]);

  // Load chat data to get handymanIds (fallback for Firestore)
  const { data: chatData } = useQuery({
    queryKey: ["chatData", chatId],
    queryFn: () => chatModel.getChat(chatId),
    enabled: !!chatId && !conversationId, // Only if not using backend
  });

  // Load handymen if this is a maintenance group chat (fallback for Firestore)
  const handymanIds = useMemo(() => chatData?.handymanIds || [], [
    chatData?.handymanIds,
  ]);
  const { data: allHandymen = [] } = useQuery({
    queryKey: ["handymen", tenantData?.ownerId],
    queryFn: () => HandymanModel.getByOwner(tenantData?.ownerId),
    enabled: !!tenantData?.ownerId && handymanIds.length > 0 && !conversationId,
  });

  // Filter handymen that are part of this chat (fallback for Firestore)
  const chatHandymen = useMemo(() => {
    if (!handymanIds.length || !allHandymen.length) return [];
    return allHandymen.filter((h) => handymanIds.includes(h.id));
  }, [handymanIds, allHandymen]);
  const debugMessages = [
    {
      id: "m1",
      sender: "ai",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
      content:
        "Hi, you can chat with your landlord and Leo AI here. What’s going on at 142 Maple St, Apt 2B?",
    },
    {
      id: "m2",
      sender: "tenant",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.96).toISOString(),
      content: "Hey, my kitchen sink has been dripping under the cabinet.",
    },
    {
      id: "m3",
      sender: "ai",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.93).toISOString(),
      content:
        "Got it. I’ve started a repair request and notified your landlord. To help maintenance prepare, could you share a few quick details?\n\n• 📍 Which room and fixture? (for example, kitchen sink or bath vanity)\n\n• 🔧 What’s wrong? (leak, drip, loose handle, no water)\n\n• ⏰ When does it happen? (always, only hot, only cold, on and off)\n\n• 👀 What do you see or hear? (water in the cabinet, a puddle, sounds or smells)\n\n• 📸 If it’s easy, a photo helps the tech.",
      actions: [
        {
          type: "create_repair_ticket",
          data: {
            title: "Kitchen sink issue",
            description: "Tenant reports sink leaking. Details pending.",
            category: "plumbing",
            priority: "medium",
            location: null,
          },
          confidence: 0.9,
          reasoning:
            "Vague plumbing report, create ticket and collect specifics.",
        },
      ],
    },
    {
      id: "m4",
      sender: "tenant",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.9).toISOString(),
      content:
        "Kitchen sink. Slow drip in the cabinet, worse when the hot is on.",
    },
    {
      id: "m5",
      sender: "ai",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.88).toISOString(),
      content:
        "Thanks. I’ve updated your ticket. If you can, keep the cabinet door open a bit and place a towel or small bowl to catch drips. We’ll coordinate a plumber and send the time window here.",
      actions: [
        {
          type: "update_repair_ticket",
          data: {
            ticketId: "recent-ticket-id",
            description:
              "Kitchen sink slow leak under basin. Worse with hot water.",
            location: "kitchen sink",
            priority: "medium",
          },
          confidence: 0.85,
          reasoning:
            "Follow up within 30 minutes, same category. Update the existing ticket.",
        },
      ],
    },
    {
      id: "m6",
      sender: "tenant",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.87).toISOString(),
      content: "Any chance someone could come today?",
    },
    {
      id: "m7",
      sender: "ai",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.85).toISOString(),
      content:
        "I'm reaching out to our plumbing vendor now for the earliest window. I’ll reply here as soon as they confirm.",
      actions: [
        {
          type: "update_repair_ticket",
          data: {
            ticketId: "recent-ticket-id",
            description: "Vendor outreach started. Awaiting ETA for visit.",
            location: "kitchen sink",
            priority: "medium",
          },
          confidence: 0.8,
          reasoning: "Progress note so the thread reflects next step.",
        },
      ],
    },
    {
      id: "m8",
      sender: "tenant",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.84).toISOString(),
      content: "Okay, thanks!",
    },
    {
      id: "m9",
      sender: "ai",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.8).toISOString(),
      content:
        "Update: the plumber can come tomorrow from 9 to 11 am. Does that work for you? I’ll lock it in and send the on the way text when they’re headed over.",
    },
    {
      id: "m10",
      sender: "tenant",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2.79).toISOString(),
      content: "Yes, that works.",
    },
  ];
  const messagesToRender = (() => {
    const base = isDebugTenantChat ? debugMessages : messages;
    if (base.length > 0) return base;
    if (!welcomeMessage || isLoadingMessages || isLoadingInitialMessage) {
      return base;
    }
    return [
      {
        id: "leo-welcome",
        sender: "ai",
        fromUserType: "ai",
        content: welcomeMessage,
        timestamp: new Date().toISOString(),
      },
    ];
  })();

  // Helper function to get unit address for a tenant
  const getUnitAddress = async (tenant) => {
    if (!tenant.unitNumber) return "Address not available";

    try {
      // Get all units for the landlord
      const units = await unitModel.getByOwner(tenant.ownerId || "unknown");
      const unit = units.find((u) => u.unitNumber === tenant.unitNumber);

      if (!unit) return "Address not available";
      return getUnitDisplayAddress(unit) || "Address not available";
    } catch (error) {
      console.error("Error getting unit address:", error);
      return "Address not available";
    }
  };

  // Load tenant data
  const loadTenantData = useCallback(async () => {
    if (!tenantId) return;

    try {
      // If we have a token, use token-based fetch (for email link access)
      let tenantData;
      if (accessToken) {
        tenantData = await fetchTenantWithToken(tenantId, accessToken);
      } else {
        tenantData = await getTenant(tenantId);
      }

      const unitAddress = await getUnitAddress(tenantData);

      setTenantInfo({
        id: tenantData.id,
        name: `${tenantData.firstName} ${tenantData.lastName}`,
        unitNumber: tenantData.unitNumber,
        unitAddress: unitAddress,
        landlordName: "Property Management Co.", // This could be fetched from landlord data
        monthlyRent: tenantData.monthlyRent,
        phone: tenantData.phone,
        email: tenantData.email,
        status: tenantData.status,
        rentDueDay: tenantData.rentDueDay,
        leaseEnd: tenantData.leaseEnd,
      });
    } catch (error) {
      console.error("Error loading tenant data:", error);
      // Fallback to mock data if real data fails
      // Note: This allows chat to work even when backend API is unavailable
      setTenantInfo({
        id: tenantId,
        name: "Tenant",
        unitNumber: "N/A",
        unitAddress: "Address not available",
        landlordName: "Property Management Co.",
        monthlyRent: 0,
        ownerId: "unknown", // Will be updated when real data loads
      });
    }
  }, [tenantId, accessToken, fetchTenantWithToken]);

  // Load welcome message from API
  const loadWelcomeMessage = useCallback(async () => {
    if (!accessToken) return; // Don't load until we have a token

    try {
      setIsLoadingInitialMessage(true);
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";

      if (!apiUrl) {
        // Fallback welcome message with "Hey Leo AI" instruction and capabilities
        setWelcomeMessage(
          "Hello! I'm Leo AI, your AI assistant. To ask me something, start your message with 'Hey Leo AI'. I can help with maintenance issues and repairs, questions about your lease and payments, and general property questions. How can I help today?"
        );
        setIsLoadingInitialMessage(false);
        return;
      }

      const response = await fetch(
        `${apiUrl}/tenant-chat/${tenantId}/welcome?token=${accessToken}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.status === 401) {
        const error = await response.json();
        console.error("Token expired or invalid:", error);
        navigate("/token-expired");
        return;
      }

      if (response.status === 403) {
        const err = await response.json().catch(() => ({}));
        if (
          err.code === "LANDLORD_TENANT_COMMS_BLOCKED" ||
          err.code === "LANDLORD_SUBSCRIPTION_CANCELED"
        ) {
          setLandlordSubscriptionCanceled(true);
          setIsLoadingInitialMessage(false);
          return;
        }
      }

      if (response.ok) {
        const data = await response.json();
        let message =
          data.welcomeMessage ||
          "Hello! I'm Leo AI, your AI assistant. To ask me something, start your message with 'Hey Leo AI'. I can help with maintenance issues and repairs, questions about your lease and payments, and general property questions. How can I help today?";
        if (searchParams.get("intent") === "request_repair") {
          message =
            "What do you need repaired? Describe the issue and I'll create a ticket for your landlord.";
        }
        setWelcomeMessage(message);
        if (data.tenantInfo) {
          setTenantInfo((prev) => ({ ...prev, ...data.tenantInfo }));
        }
        setIsLoadingInitialMessage(false);
      } else if (response.status === 404) {
        // Endpoint doesn't exist yet, use fallback message
        console.log(
          "[TenantChat] Welcome endpoint not found, using fallback message"
        );
        const fallback =
          searchParams.get("intent") === "request_repair"
            ? "What do you need repaired? Describe the issue and I'll create a ticket for your landlord."
            : "Hello! I'm Leo AI, your AI assistant. To ask me something, start your message with 'Hey Leo AI'. I can help with maintenance issues and repairs, questions about your lease and payments, and general property questions. How can I help today?";
        setWelcomeMessage(fallback);
        setIsLoadingInitialMessage(false);
      } else {
        throw new Error("Failed to load welcome message");
      }
    } catch (error) {
      console.error("Error loading welcome message:", error);
      setInitialMessageError(true);
      const fallback =
        searchParams.get("intent") === "request_repair"
          ? "What do you need repaired? Describe the issue and I'll create a ticket for your landlord."
          : "Hello! I'm Leo AI, your AI assistant. To ask me something, start your message with 'Hey Leo AI'. I can help with maintenance issues and repairs, questions about your lease and payments, and general property questions. How can I help today?";
      setWelcomeMessage(fallback);
      setIsLoadingInitialMessage(false);
    }
  }, [tenantId, accessToken, navigate, searchParams]);

  // Load chat history from API - DEPRECATED (now using React Query)
  const loadChatHistory = useCallback(async () => {
    // This function is no longer needed since we're using React Query for messages
    console.log("loadChatHistory is deprecated - using React Query instead");
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    console.log("🔔 Notification button clicked!");
    console.log("Requesting notification permission...");

    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      alert("This browser does not support notifications");
      return;
    }

    console.log("Current notification permission:", Notification.permission);

    if (Notification.permission === "granted") {
      console.log("Notifications already granted");
      setNotificationPermission("granted");
      return;
    }

    if (Notification.permission !== "denied") {
      console.log("Requesting permission...");
      try {
        const permission = await Notification.requestPermission();
        console.log("Permission result:", permission);
        setNotificationPermission(permission);

        if (permission === "granted") {
          console.log("✅ Notifications enabled successfully!");
          // Show a test notification
          new Notification("Notifications Enabled!", {
            body: "You will now receive notifications for new messages.",
            icon: "/logo192.png",
          });
        } else {
          console.log("❌ Notifications denied by user");
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error);
        setNotificationPermission("denied");
      }
    } else {
      console.log("Notifications previously denied");
      setNotificationPermission("denied");
    }
  }, []);

  // Show notification for new messages
  const showNotification = useCallback(
    (message) => {
      if (notificationPermission !== "granted") return;

      const notification = new Notification("New Message from Leo AI", {
        body:
          message.text.length > 100
            ? message.text.substring(0, 100) + "..."
            : message.text,
        icon: "/logo192.png", // Use your app icon
        badge: "/logo192.png",
        tag: "leo-message",
        requireInteraction: false,
        silent: false,
      });

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    },
    [notificationPermission]
  );

  // Initialize tenant data and load welcome message
  useEffect(() => {
    if (!tenantId || !accessToken) return;

    // Request notification permission
    requestNotificationPermission();

    // Load tenant data and welcome message
    loadTenantData();
    loadWelcomeMessage();
  }, [
    tenantId,
    accessToken,
    loadTenantData,
    loadWelcomeMessage,
    requestNotificationPermission,
  ]);

  // Create or fetch backend conversation for this tenant
  useEffect(() => {
    const initConversation = async () => {
      if (!tenantData || !accessToken) return;
      try {
        const apiUrl =
          process.env.REACT_APP_API_URL || "http://localhost:4000/api";
        if (!apiUrl) return;

        // Always fetch fresh conversation from backend to ensure correctness
        // The backend API is idempotent - it returns the same conversationId for the same tenantId+landlordId
        // We still check cache first for performance, but always verify with backend
        const cacheKey = `conv-${tenantId}`;
        const cached = localStorage.getItem(cacheKey);

        // For token-based access, always fetch fresh (don't trust cache)
        // For authenticated users, we can use cache but will verify with backend
        if (accessToken) {
          // Token-based access: always fetch fresh to ensure correct conversation
          console.log(
            "[TenantChat] Token-based access - fetching fresh conversation from backend"
          );
        } else if (cached) {
          // Authenticated user: can use cache for initial load, but backend will verify
          console.log(
            "[TenantChat] Found cached conversationId, will verify with backend:",
            cached
          );
        }

        console.log(
          "[TenantChat] Creating conversation at /conversations/create",
          {
            tenantId,
            landlordId: tenantData?.ownerId,
            unitId: tenantData?.unitId || null,
            propertyId: tenantData?.propertyId || null,
          }
        );
        // Build request body, only including unitId/propertyId if they exist and are valid
        const requestBody = {
          tenantId: tenantId,
          landlordId: tenantData.ownerId,
        };

        // Only include unitId if it exists and is not empty
        if (
          tenantData.unitId &&
          tenantData.unitId.trim &&
          tenantData.unitId.trim() !== ""
        ) {
          requestBody.unitId = tenantData.unitId.trim();
        } else if (
          tenantData.unitId &&
          typeof tenantData.unitId === "string" &&
          tenantData.unitId !== ""
        ) {
          requestBody.unitId = tenantData.unitId;
        }

        // Only include propertyId if it exists and is not empty
        if (
          tenantData.propertyId &&
          tenantData.propertyId.trim &&
          tenantData.propertyId.trim() !== ""
        ) {
          requestBody.propertyId = tenantData.propertyId.trim();
        } else if (
          tenantData.propertyId &&
          typeof tenantData.propertyId === "string" &&
          tenantData.propertyId !== ""
        ) {
          requestBody.propertyId = tenantData.propertyId;
        }

        console.log(
          "[TenantChat] Request body for /conversations/create:",
          requestBody
        );

        const res = await fetch(`${apiUrl}/conversations/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });
        const data = await res.json();
        console.log("[TenantChat] /create response", res.status, data);
        if (res.ok && data.conversationId) {
          // ALWAYS save conversationId to localStorage so landlord can find it
          // The backend API is idempotent, so this is safe
          localStorage.setItem(cacheKey, data.conversationId);
          console.log(
            "[TenantChat] Saved conversationId to localStorage for landlord sync:",
            data.conversationId
          );
          setConversationId(data.conversationId);
        } else {
          console.error("Failed to create/find conversation", data);
        }
      } catch (err) {
        console.error("Error creating conversation:", err);
      }
    };
    initConversation();
  }, [tenantId, tenantData, accessToken]);

  // Load chat history when component mounts
  useEffect(() => {
    if (tenantId && !hasLoadedInitialMessage) {
      loadChatHistory();
      setHasLoadedInitialMessage(true);
    }
  }, [tenantId, hasLoadedInitialMessage, loadChatHistory]);

  // Track if user has scrolled up
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
      setUserScrolledUp(!isAtBottom);
    };

    // Check initial scroll position
    handleScroll();

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll to bottom when new messages arrive (only if user is at bottom or force scroll)
  useEffect(() => {
    if (isDebugTenantChat) return; // keep viewport stable during screenshots/recordings

    const container = messagesContainerRef.current;
    if (!container) return;

    // Check if we should force scroll (e.g., user just sent a message, typing indicator appeared)
    if (shouldForceScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      // Don't reset the flag yet - keep it true until we're done with the response cycle
      return;
    }

    // Check if user is at bottom before scrolling
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;

    // Only scroll if user is at the bottom (or very close to it)
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, isTyping, isDebugTenantChat]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!isLoadingMessages && messages.length > 0) {
      // Scroll to bottom immediately when messages first load
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 100);
    }
  }, [isLoadingMessages, messages.length]); // Run when loading state or messages change

  // Focus input when initial message is loaded
  useEffect(() => {
    if (!isLoadingInitialMessage && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isLoadingInitialMessage]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && selectedImages.length === 0) || !chatId) {
      console.warn(
        "[TenantChat] Blocked send: empty message or missing chatId",
        { hasText: !!newMessage.trim(), chatId }
      );
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (lastMessageTimeRef.current && now - lastMessageTimeRef.current < 1000) {
      message.warning("Please wait before sending another message");
      return;
    }
    lastMessageTimeRef.current = now;

    // Store message content and images before clearing
    const messageContent = newMessage;
    const messageImages = [...selectedImages];
    const imageUrls = messageImages.map((img) => img.url);

    // Clear form immediately for better UX
    setNewMessage("");
    setSelectedImages([]);
    setShowImageUpload(false);

    // Force scroll to bottom when user sends a message
    shouldForceScrollRef.current = true;

    // Track message sent
    trackEvent("tenant_message_sent", {
      tenantId: tenantId,
      messageLength: messageContent.length,
      hasImages: messageImages.length > 0,
      imageCount: messageImages.length,
    });

    // Detect if message should route to Leo AI ("hey leo" or "hey leo ai", case-insensitive)
    const isForLeo = /^\s*hey\s+leo(\s+ai)?\b/i.test(messageContent);

    console.log("Sending message with images:", imageUrls);

    // Create optimistic message object
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      sender: "tenant",
      content: messageContent,
      timestamp: new Date().toISOString(),
      images: imageUrls,
      metadata: { isForLeo },
    };

    // Optimistically update the UI - use the correct query key based on whether we're using backend
    const messagesQueryKey = conversationId
      ? ["tenantBackendMessages", conversationId]
      : chatKeys.messages(chatId);
    queryClient.setQueryData(messagesQueryKey, (oldMessages = []) => {
      // Keep the on-load Leo welcome visible until the backend history refetches
      const seeded =
        oldMessages.length === 0 && welcomeMessage
          ? [
              {
                id: "leo-welcome",
                sender: "ai",
                fromUserType: "ai",
                content: welcomeMessage,
                timestamp: new Date().toISOString(),
              },
            ]
          : oldMessages;
      return [...seeded, optimisticMessage];
    });

    try {
      setIsTyping(true);

      // Send to backend via chatModel helper and append AI reply if present.
      // MHMDA: Do NOT save to Firestore before backend; when consent_required the backend never runs, so we must not persist the message.
      let messageSavedToFirestore = false;
      try {
        console.log("[TenantChat] Calling sendTenantMessageViaBackend");
        try {
          const sendData = await chatModel.sendTenantMessageViaBackend({
            tenantId,
            landlordId: tenantData.ownerId,
            message: messageContent, // Can be empty string if only images
            images: imageUrls,
            isForLeo,
          });
          console.log("[TenantChat] send-message response", sendData);

          // MHMDA: 200 + consent_required: show consent modal, roll back optimistic message, do not show AI reply. Message was not saved (we call backend before Firestore).
          if (
            sendData?.consent_required === true ||
            sendData?.consentRequired === true
          ) {
            const messagesQueryKey = conversationId
              ? ["tenantBackendMessages", conversationId]
              : chatKeys.messages(chatId);
            queryClient.setQueryData(messagesQueryKey, (oldMessages = []) =>
              oldMessages.filter((msg) => msg.id !== optimisticMessage.id)
            );
            setHealthConsentModalVisible(true);
            setIsTyping(false);
            await refetchMessages();
            return;
          }

          messageSavedToFirestore = true; // Backend succeeded and saved the message

          // SB 6284: When adverse decision is held for human review, show required placeholder (no AI denial text)
          const isAwaitingHumanReview =
            sendData?.aiResponse?.status === "awaiting_human_review";
          const pendingReviewMessage =
            "This request has been logged and is pending review by the property manager.";
          const aiText = isAwaitingHumanReview
            ? pendingReviewMessage
            : sendData?.aiResponse?.text || null;
          if (aiText) {
            console.log(
              "[TenantChat] Appending AI reply to UI:",
              aiText?.slice(0, 120)
            );
            await chatModel.addMessage(
              chatId,
              "ai",
              aiText,
              {
                actions: sendData?.aiResponse?.actions || [],
                repairTicketId: sendData?.aiResponse?.repairTicketId || null,
              },
              []
            );
          }
        } catch (backendError) {
          // If backend fails but message is already in Firestore, don't show error
          // The message will still appear because it's in Firestore
          console.warn(
            "[TenantChat] Backend API call failed, but message is in Firestore:",
            backendError
          );
          // Don't show error to user - message is already saved and will appear
          // The backend will eventually sync, or we can retry later
        }

        // Refetch to get the real messages - use the correct query key
        const messagesQueryKey = conversationId
          ? ["tenantBackendMessages", conversationId]
          : chatKeys.messages(chatId);

        // Get current messages from cache to preserve them during refetch
        const currentMessages =
          queryClient.getQueryData(messagesQueryKey) || [];
        console.log(
          "[TenantChat] Current messages in cache before refetch:",
          currentMessages.length
        );

        // Instead of invalidating (which clears cache), use refetch which preserves cache during fetch
        // The queryFn will merge cached messages with new ones if backend returns too few
        await refetchMessages();
      } catch (err) {
        console.error("Error sending message:", err);

        // Only rollback and show error if message wasn't saved to Firestore
        if (!messageSavedToFirestore) {
          // Rollback optimistic update - use the correct query key
          const messagesQueryKey = conversationId
            ? ["tenantBackendMessages", conversationId]
            : chatKeys.messages(chatId);
          queryClient.setQueryData(messagesQueryKey, (oldMessages = []) => {
            return oldMessages.filter((msg) => msg.id !== optimisticMessage.id);
          });
          // If Firestore save failed, restore the message to the input
          setNewMessage(messageContent);
          setSelectedImages(messageImages);
          message.error("Failed to send message. Please try again.");
        } else {
          // Message is in Firestore, just refetch to show it (don't show error)
          console.log(
            "[TenantChat] Message saved to Firestore, refetching even though backend call failed"
          );
          // Use refetch instead of invalidate to preserve existing messages
          await refetchMessages();
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Rollback optimistic update - use the correct query key
      const messagesQueryKey = conversationId
        ? ["tenantBackendMessages", conversationId]
        : chatKeys.messages(chatId);
      queryClient.setQueryData(messagesQueryKey, (oldMessages = []) => {
        return oldMessages.filter((msg) => msg.id !== optimisticMessage.id);
      });
      // Restore message on error
      setNewMessage(messageContent);
      setSelectedImages(messageImages);
      message.error("Failed to send message");
    } finally {
      setIsTyping(false);
      // Reset force scroll flag after the response cycle is complete
      setTimeout(() => {
        shouldForceScrollRef.current = false;
      }, 100);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Just now";
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const shortDate = date.toLocaleDateString([], {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
    const shortTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    const now = new Date();
    const diffInMinutes = (now - date) / (1000 * 60);
    const diffInHours = diffInMinutes / 60;

    if (diffInMinutes < 1) {
      return `${shortDate} ${shortTime}`;
    }
    if (diffInMinutes < 60) {
      const minutes = Math.floor(diffInMinutes);
      return `${shortDate} ${shortTime} · ${minutes} ${
        minutes === 1 ? "min" : "min"
      } ago`;
    }
    if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${shortDate} ${shortTime} · ${hours} ${
        hours === 1 ? "hr" : "hr"
      } ago`;
    }
    return `${shortDate} ${shortTime}`;
  };

  const getSenderName = (sender, message = null) => {
    // If message has fromUserId and fromUserType is vendor, try to get name from participants
    if (sender === "vendor" && message && message.fromUserId) {
      const vendorParticipant = conversationParticipants.find(
        (p) =>
          p.userId === message.fromUserId &&
          (p.userType === "vendor" || p.userType === "handyman")
      );
      if (vendorParticipant) {
        return (
          vendorParticipant.userName ||
          vendorParticipant.userEmail ||
          "Service Team"
        );
      }
      return "Service Team"; // Fallback if participant not found
    }

    switch (sender) {
      case "ai":
        return "Leo AI";
      case "tenant":
        return "You";
      case "landlord":
        return "Landlord";
      case "vendor":
        return "Service Team";
      default:
        return "Unknown";
    }
  };

  const getSenderIcon = (sender) => {
    switch (sender) {
      case "ai":
        return (
          <img
            src="/icons/leo/leo.png"
            alt="Leo AI"
            style={{
              width: "16px",
              height: "16px",
              filter: "brightness(0) invert(1)",
            }}
          />
        );
      case "tenant":
        return (
          <UserOutlined
            style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
          />
        );
      case "landlord":
        return (
          <UserOutlined
            style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
          />
        );
      default:
        return (
          <UserOutlined
            style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
          />
        );
    }
  };

  // Don't render until we have a valid access token
  if (!accessToken && !isDebugTenantChat) {
    return (
      <ChatContainer>
        <LoadingSpinnerContainer>
          <Spin size="large" />
          <LoadingText>Verifying access...</LoadingText>
        </LoadingSpinnerContainer>
      </ChatContainer>
    );
  }

  if (landlordSubscriptionCanceled) {
    return (
      <ChatUnavailablePage>
        <ChatUnavailableLogoBar>
          <Logo
            color="white"
            size="medium"
            linkTo={null}
            style={{ pointerEvents: "none" }}
          />
        </ChatUnavailableLogoBar>
        <ChatUnavailableMain>
          <BlockedTitle level={3}>Chat unavailable</BlockedTitle>
          <SubscriptionCanceledAlert
            type="warning"
            description={
              <span>
                This chat is not available because your landlord&apos;s
                LandlordOps subscription is inactive or was canceled. If you
                believe this is a mistake, please contact your landlord
                directly.
              </span>
            }
          />
        </ChatUnavailableMain>
      </ChatUnavailablePage>
    );
  }

  return (
    <ChatContainer>
      {/* Header */}
      <ChatHeader>
        <HeaderContent>
          <Logo
            color="white"
            size="medium"
            linkTo={null}
            style={{ pointerEvents: "none !important" }}
          />
          <HeaderTitle>Chat with Your Landlord & Leo AI</HeaderTitle>
          {/* Participant List */}
          <ParticipantsList>
            <ParticipantItem>
              <ParticipantTags>
                <ParticipantTag>You</ParticipantTag>
                <ParticipantTag>Landlord</ParticipantTag>
                {chatData?.leoEnabled !== false && (
                  <ParticipantTag>
                    <img
                      src="/icons/leo/leo.png"
                      alt="Leo AI"
                      style={{
                        marginRight: "4px",
                        width: "12px",
                        height: "12px",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                    Leo AI
                  </ParticipantTag>
                )}
                {/* Show vendors from backend conversation participants or from handymen (fallback) */}
                {(() => {
                  const vendorParticipants = conversationParticipants.filter(
                    (p) => p.userType === "vendor" || p.userType === "handyman"
                  );

                  if (vendorParticipants.length > 0) {
                    return vendorParticipants.map((vendor) => (
                      <ParticipantTag key={vendor.userId}>
                        <ToolOutlined
                          style={{
                            marginRight: "4px",
                            fontSize: "0.7rem",
                            filter: "brightness(0) invert(1)",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {vendor.userName ||
                            vendor.userEmail ||
                            "Service Team"}
                        </span>
                        <span
                          style={{
                            marginLeft: "4px",
                            fontSize: "0.65rem",
                            opacity: 0.7,
                            fontWeight: 400,
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                        >
                          (Service Team)
                        </span>
                      </ParticipantTag>
                    ));
                  }

                  // Fallback to handymen from Firestore if no backend participants
                  return chatHandymen.map((handyman) => (
                    <ParticipantTag key={handyman.id}>
                      <ToolOutlined
                        style={{
                          marginRight: "4px",
                          fontSize: "0.7rem",
                          filter: "brightness(0) invert(1)",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ whiteSpace: "nowrap" }}>
                        {handyman.name}
                      </span>
                      <span
                        style={{
                          marginLeft: "4px",
                          fontSize: "0.65rem",
                          opacity: 0.7,
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        (Service Team)
                      </span>
                    </ParticipantTag>
                  ));
                })()}
              </ParticipantTags>
            </ParticipantItem>
          </ParticipantsList>
          <NotificationSettings>
            {notificationPermission === "default" && (
              <NotificationButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("🔔 Button clicked!");
                  requestNotificationPermission();
                }}
              >
                <BellOutlined style={{ filter: "brightness(0) invert(1)" }} />{" "}
                <span>Enable Notifications</span>
              </NotificationButton>
            )}
            {notificationPermission === "denied" && (
              <NotificationStatus>
                <BellOutlined
                  style={{ opacity: 0.5, filter: "brightness(0) invert(1)" }}
                />{" "}
                <span>Notifications blocked</span>
              </NotificationStatus>
            )}
            {notificationPermission === "granted" && (
              <NotificationStatus>
                <BellFilled style={{ filter: "brightness(0) invert(1)" }} />{" "}
                <span>Notifications enabled</span>
              </NotificationStatus>
            )}
          </NotificationSettings>
        </HeaderContent>
      </ChatHeader>

      {/* Messages */}
      <MessagesContainer ref={messagesContainerRef}>
        {isLoadingInitialMessage && (
          <MessageBubble $sender="assistant">
            <MessageAvatar $sender="assistant">
              <Avatar
                size="small"
                icon={
                  <LogoImage
                    src={logoImage}
                    alt="LandlordOps"
                    className="message"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                }
                style={{ backgroundColor: "var(--color-primary)" }}
              />
            </MessageAvatar>
            <MessageContent $sender="assistant">
              <MessageText
                $sender="assistant"
                style={{ fontStyle: "italic", color: "#6b7280" }}
              >
                Loading...
              </MessageText>
            </MessageContent>
          </MessageBubble>
        )}

        {isLoadingMessages && !isLoadingInitialMessage && (
          <LoadingSpinnerContainer>
            <Spin size="large" />
            <LoadingText>Loading messages...</LoadingText>
          </LoadingSpinnerContainer>
        )}

        {messagesToRender.map((message, index) => {
          // Debug: Log first AI message specifically
          if (index === 0 && message.sender === "ai") {
            console.log("[TenantChat] 🔍 First AI message being rendered:", {
              id: message.id,
              sender: message.sender,
              fromUserType: message.fromUserType,
              content: message.content,
              contentLength: message.content?.length,
              contentType: typeof message.content,
              hasContent: !!message.content,
              trimmedContent: message.content
                ? String(message.content).trim()
                : null,
              willRender: !!(message.content && String(message.content).trim()),
              timestamp: message.timestamp,
              fullMessage: message,
            });
          }

          return (
            <MessageBubble key={message.id} $sender={message.sender}>
              <MessageAvatar $sender={message.sender}>
                <Avatar
                  size="small"
                  icon={getSenderIcon(message.sender)}
                  style={{
                    backgroundColor:
                      message.sender === "ai"
                        ? "var(--color-primary)"
                        : message.sender === "landlord"
                        ? "#2C5F2D"
                        : message.sender === "vendor"
                        ? "#4A7C59"
                        : "#2C5F2D",
                  }}
                />
              </MessageAvatar>
              <MessageContent $sender={message.sender}>
                <MessageHeader $sender={message.sender}>
                  <MessageSender>
                    {getSenderName(message.sender, message)}
                  </MessageSender>
                  <MessageTime>{formatTime(message.timestamp)}</MessageTime>
                </MessageHeader>
                {message.content && String(message.content).trim() && (
                  <MessageText $sender={message.sender}>
                    {renderLeoMarkdown(message.content)}
                  </MessageText>
                )}
                {message.images && message.images.length > 0 && (
                  <MessageImages>
                    {message.images.map((imageUrl, index) => (
                      <MessageImage
                        key={index}
                        src={imageUrl}
                        alt={`Image ${index + 1}`}
                        onClick={() => window.open(imageUrl, "_blank")}
                      />
                    ))}
                  </MessageImages>
                )}
                {message.actions && message.actions.length > 0 && (
                  <ActionIndicator>
                    {message.actions.map((action, index) => (
                      <ActionBadge key={index}>
                        {action.type === "create_repair_ticket" &&
                          "🔧 Repair ticket created"}
                      </ActionBadge>
                    ))}
                  </ActionIndicator>
                )}
              </MessageContent>
            </MessageBubble>
          );
        })}

        {isTyping && chatData?.leoEnabled !== false && (
          <MessageBubble sender="assistant">
            <MessageAvatar sender="assistant">
              <Avatar
                size="small"
                icon={
                  <LogoImage
                    src={logoImage}
                    alt="LandlordOps"
                    className="message"
                  />
                }
                style={{
                  backgroundColor: "var(--color-primary)",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </MessageAvatar>
            <MessageContent sender="assistant">
              <TypingIndicator>
                Leo AI is typing
                <TypingDots>
                  <span></span>
                  <span></span>
                  <span></span>
                </TypingDots>
              </TypingIndicator>
            </MessageContent>
          </MessageBubble>
        )}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Input Area */}
      <InputArea>
        {showImageUpload && (
          <ImageUploadSection>
            <ImageUpload
              onImagesChange={setSelectedImages}
              maxImages={3}
              disabled={isLoadingInitialMessage || isTyping}
            />
          </ImageUploadSection>
        )}

        <InputContainer>
          <InputActions>
            <AttachButton
              type="text"
              icon={<PaperClipOutlined />}
              onClick={() => setShowImageUpload(!showImageUpload)}
              disabled={isLoadingInitialMessage || isTyping}
              $active={showImageUpload}
            />
          </InputActions>

          <TextArea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={
              isLoadingInitialMessage
                ? "Loading..."
                : "Chat with landlord & Leo AI..."
            }
            title="Please do not share sensitive medical or health information."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={isLoadingInitialMessage || isTyping}
            onKeyPress={handleKeyPress}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <SendButton
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendMessage}
            loading={isTyping}
            disabled={
              isDebugTenantChat ||
              (!newMessage.trim() && selectedImages.length === 0) ||
              isLoadingInitialMessage ||
              isTyping
            }
          >
            <span className="desktop-text">Send</span>
          </SendButton>
        </InputContainer>

        {/* Disclaimer */}
        <Disclaimer>
          <DisclaimerText>
            <span className="desktop-text" style={{ maxWidth: "1000px" }}>
              Please do not share sensitive medical or health information. Leo
              AI can make mistakes. Check important info.
            </span>
            <span className="mobile-text">
              Please do not share sensitive medical or health information. Leo
              AI can make mistakes. Check important info.
            </span>
          </DisclaimerText>
        </Disclaimer>
      </InputArea>
      <HealthConsentModal
        open={healthConsentModalVisible}
        onAccept={() => setHealthConsentModalVisible(false)}
        onCancel={() => setHealthConsentModalVisible(false)}
      />
    </ChatContainer>
  );
}

export default function TenantChat() {
  const { propertySlug } = useParams();
  if (propertySlug) {
    return <TenantChatPublicLeo />;
  }
  return <TenantChatWithToken />;
}
